import { useState, useEffect, useRef, useCallback } from "react";
import {
  ORG_KEYS,
  subscribeOrgData,
  getActiveLegalUnit,
  ensureActiveLegalUnit,
} from "./globalData";
import { getAllEmployee, getSelfEmployee } from "./employee";
import { getDepartmentsForActiveEntity } from "./department";
import { getAllGroups } from "./employeeGroups";
import { getAllShift } from "./shift";
import { getLegalUnit } from "./auth";
import { getGeofence } from "./geofence";

/**
 * React hooks for org-wide reference data backed by the sessionStorage cache in
 * globalData.js. A hook:
 *   - reads the cached value synchronously on mount (no flash if already cached),
 *   - fetches-if-missing (deduped across components mounting together),
 *   - subscribes to the invalidation bus, so when a setter emits a fresh value
 *     or an `invalidateOrg*()` clears the cache, the component re-reads/refetches.
 *
 * Usage:
 *   const { data: employees, loading, error, refresh } = useOrgEmployees();
 */

const readCache = (key) => {
  try {
    const v = sessionStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, val) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* storage full / unavailable — ignore, value stays uncached */
  }
};

// Dedupe concurrent fetches for the same resource.
const inflight = {};

const ensure = async (key, fetcher) => {
  const cached = readCache(key);
  if (cached) return cached;
  if (!inflight[key]) {
    inflight[key] = (async () => {
      try {
        const res = await fetcher();
        const data = res?.data ?? (Array.isArray(res) ? res : null);
        if (data) writeCache(key, data);
        return data;
      } finally {
        delete inflight[key];
      }
    })();
  }
  return inflight[key];
};

function useOrgResource(key, fetcher) {
  const [data, setData] = useState(() => readCache(key));
  const [loading, setLoading] = useState(() => !readCache(key));
  const [error, setError] = useState(null);

  // Keep the latest fetcher without making `load` change identity.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await ensure(key, fetcherRef.current);
      setData(d);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    let active = true;
    const current = readCache(key);
    if (current) {
      setData(current);
      setLoading(false);
    } else {
      load();
    }

    const unsubscribe = subscribeOrgData(key, () => {
      if (!active) return;
      const next = readCache(key);
      if (next) {
        // A setter emitted a fresh value — adopt it without a refetch.
        setData(next);
      } else {
        // Invalidated — refetch.
        load();
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [key, load]);

  return { data, loading, error, refresh: load };
}

/**
 * The cache as a plain async call, for code that fetches outside React's
 * lifecycle — typically several resources at once inside a Promise.all, where
 * a hook cannot go. Returns the array directly, cached and deduped, so a
 * screen that already has the directory in hand does not ask for it again.
 *
 * Prefer the hooks below where the shape allows: these callers get the cache
 * but not the invalidation bus, so they see a change on their next load rather
 * than immediately.
 */
export const ensureOrgEmployees = () => ensure(ORG_KEYS.employees, getAllEmployee);
export const ensureSelfEmployee = () => ensure(ORG_KEYS.self, getSelfEmployee);
export const ensureOrgDepartments = () =>
  ensure(ORG_KEYS.departments, getDepartmentsForActiveEntity);
export const ensureOrgGroups = () => ensure(ORG_KEYS.groups, getAllGroups);
export const ensureOrgShifts = () => ensure(ORG_KEYS.shifts, getAllShift);
export const ensureLegalUnits = () => ensure(ORG_KEYS.legalUnits, getLegalUnit);
export const ensureGeofences = () => ensure(ORG_KEYS.geofences, getGeofence);

export const useOrgEmployees = () => useOrgResource(ORG_KEYS.employees, getAllEmployee);

/**
 * The signed-in employee's own record, in full.
 *
 * Not the same question as "who is in this organisation": the directory is a
 * colleague's view and omits most of a person, so anything asking about
 * yourself — onboarding state, your own bank details, your joining date — has
 * to come from here.
 */
export const useSelfEmployee = () => useOrgResource(ORG_KEYS.self, getSelfEmployee);
export const useOrgDepartments = () =>
  useOrgResource(ORG_KEYS.departments, getDepartmentsForActiveEntity);
export const useOrgGroups = () => useOrgResource(ORG_KEYS.groups, getAllGroups);
export const useOrgShifts = () => useOrgResource(ORG_KEYS.shifts, getAllShift);
export const useLegalUnits = () => useOrgResource(ORG_KEYS.legalUnits, getLegalUnit);
export const useGeofences = () => useOrgResource(ORG_KEYS.geofences, getGeofence);

/**
 * Subscribe to the active legal entity. Re-renders when the admin switches
 * entities (via the bus). Entity-scoped screens can key fetches on `entity?._id`.
 */
export const useActiveLegalUnit = () => {
  const [entity, setEntity] = useState(() => getActiveLegalUnit());

  useEffect(() => {
    ensureActiveLegalUnit();
    setEntity(getActiveLegalUnit());
    const unsubscribe = subscribeOrgData(ORG_KEYS.activeLegalUnit, () => {
      setEntity(getActiveLegalUnit());
    });
    // also resync if the entity list itself changes (e.g. first one loaded)
    const unsubscribeList = subscribeOrgData(ORG_KEYS.legalUnits, () => {
      ensureActiveLegalUnit();
      setEntity(getActiveLegalUnit());
    });
    return () => {
      unsubscribe();
      unsubscribeList();
    };
  }, []);

  return entity;
};
